<template>
<div>
    <div style="display: flex">
        <template v-if="!settings.dropdown">
            <Cinput :value="hoursInput"
                    :lastpass="lastpass"
                    :data-qa="dataQa ? 'hours.' + dataQa : null"
                    :focused="hoursFocused"
                    input-pattern="\d*"
                    size="2"
                    :min-size="true"
                    maxlength="2"
                    :placeholder="$trl('aw.hours')"
                    :invalid="hoursInvalid && validation_highlight"
                    @input="value => {hoursInput = value.trim()}"
                    @focus="() => {$emit('focus'); hoursFocused=true;}"
                    @blur="() => {$emit('blur'); hoursFocused=false;}"
                    @finished="() => {minutesFocused = true}"/>
            <span class="odr-input__field__time">:</span>
            <Cinput :value="minutesInput"
                    :lastpass="lastpass"
                    input-pattern="\d*"
                    :data-qa="dataQa ? 'minutes.' + dataQa : null"
                    :focused="minutesFocused"
                    size="2"
                    :min-size="true"
                    maxlength="2"
                    :placeholder="$trl('aw.minutes')"
                    :invalid="minutesInvalid && validation_highlight"
                    @input="value => {minutesInput = value.trim()}"
                    @focus="() => {$emit('focus'); minutesFocused=true;}"
                    @blur="() => {$emit('blur'); minutesFocused=false;}"
                    @finished="() => {$emit('finished')}"/>
        </template>
        <template v-else>
            <block :id="id"
                   :stateId="id"
                   :value="time"
                   :values="timeItems"
                   type="options"
                   :settings="settings"
                   :suggest="settings.suggest"
                   @input="(value) =>optionSelected(value)"/>
        </template>
    </div>
    <div v-if="validation_showError && validation_error"
         class="odr-error-message"
         :class="{
             'odr-error-message_plain': $root.appConfig.mode !== 'chat'
         }">
        {{ validation_error }}
    </div>
</div>
</template>

<script>
import '@/scss/blocks/error-message.scss';
import '@/scss/blocks/input.scss';

import Cinput from '@/vue/blocks/input.vue';
import block from '@/vue/block.vue';
import validationMixin from '@/utils/mixins/validation';
import focusMixin from '@/utils/mixins/focus';
import { date2str, str2date, TIME_FORMAT, zeroPad } from '@/utils/format';

export default {
    components: {
        Cinput,
        block,
    },
    mixins: [validationMixin, focusMixin],
    props: {
        id: {
            type: String,
        },
        settings: {
            type: Object,
            default: null,
        },
        dataQa: {
            type: String,
        },
        value: {
            type: String,
            default: null,
        },
        lastpass: {
            type: String,
        },
    },
    data () {
        const is24Hour = this.settings.use24HourFormat ?? !this.$root.appConfig.ampm;
        return {
            hoursFocused: false,
            minutesFocused: false,
            is24Hour,
            selectedOption: this.value,
            timeFormat: is24Hour ? '%H:%M' : '%h:%M #ampm',
        };
    },
    computed: {
        hoursInput () {
            const value = this.settings.dropdown ? this.selectedOption : this.value;
            const date = value ? str2date(value, TIME_FORMAT) : null;
            return date ? String(date.getHours()) : '';
        },
        minutesInput () {
            const value = this.settings.dropdown ? this.selectedOption : this.value;
            const date = value ? str2date(value, TIME_FORMAT) : null;
            return date ? String(date.getMinutes()) : '';
        },
        time () {
            if (!this.validation_error && this.hoursInput && this.minutesInput) {
                return `${this.hoursInput}:${zeroPad(Number(this.minutesInput), 2)}`;
            }
            return null;
        },
        timeItems () {
            const options = [];
            if (this.settings && this.settings.dropdown) {
                const startTime = this.settings.start ? str2date(this.settings.start, TIME_FORMAT) : str2date('0:00', TIME_FORMAT);
                const endTime = this.settings.end ? str2date(this.settings.end, TIME_FORMAT) : str2date('23:45', TIME_FORMAT);
                const minuteStep = this.settings.minuteStep && this.settings.minuteStep > 0 ? this.settings.minuteStep : 15;
                // Default option
                options.push({
                    title: `--:-- ${!this.is24Hour ? this.$trl('aw.am') : ''}`,
                    id: null,
                });
                let currentOption = startTime;
                do {
                    options.push({
                        title: date2str(currentOption, this.timeFormat),
                        id: `${currentOption.getHours()}:${zeroPad(currentOption.getMinutes(), 2)}`,
                    });
                    const currentOptionTime = currentOption.getTime();
                    const nextStep = minuteStep * 60 * 1000;
                    currentOption = new Date(currentOptionTime + nextStep);
                }
                while (currentOption.getTime() >= startTime.getTime() &&
                currentOption.getTime() <= endTime.getTime()); // check if the next option is still in range
            }
            return options;
        },
        hoursInvalid () {
            let value = parseInt(this.hoursInput, 10);
            return (this.minutesInput && !this.hoursInput) ||
                   !!this.hoursInput && (isNaN(value) || value < 0 || value > 24);
        },
        minutesInvalid () {
            let value = parseInt(this.minutesInput, 10);
            return (this.hoursInput && !this.minutesInput) ||
                   !!this.minutesInput && (isNaN(value) || value < 0 || value > 60);
        },
    },
    watch: {
        value (value) {
            this.selectedOption = value;
        },
        time (value) {
            this.$emit('input', value);
        },
        hours () {
            this.validation_changed();
        },
        minutes () {
            this.validation_changed();
        },
    },
    methods: {
        optionSelected (value) {
            const time = value ? str2date(value, TIME_FORMAT) : '';
            const hours = time ? time.getHours().toString() : null;
            const minutes = time ? zeroPad(time.getMinutes(), 2) : null;
            this.selectedOption = hours && minutes ? `${hours}:${minutes}` : null;
        },
        focus_focus () {
            if (!this.hoursFocused && !this.minutesFocused) {
                this.hoursFocused = true;
            }
        },
        validation_focusError () {
            if (!this.hoursInput || this.hoursInvalid) {
                this.hoursFocused = true;
            } else {
                this.minutesFocused = true;
            }
        },
        validation_invalid () {
            return (this.hoursInvalid && this.$trl('aw.hours_invalid')) ||
                   (this.minutesInvalid && this.$trl('aw.minutes_invalid'));
        },
        validation_isEmpty () {
            return !this.hoursInput || !this.minutesInput;
        },
    },
};
</script>
